import type { ApiResponse } from "@/models/api-response";
import type { AxiosError, AxiosInstance, AxiosRequestConfig, Method } from "axios";
import axios from "axios";
import { env } from "@/config/environment-vars";

export class ApiRequestError extends Error {
  status?: number;
  original?: unknown;

  constructor(message: string, options?: { status?: number; original?: unknown }) {
    super(message);
    this.name = "ApiRequestError";
    this.status = options?.status;
    this.original = options?.original;
  }
}

function getApiErrorMessage(error: AxiosError, url: string): string {
  const data = error.response?.data as { error?: { message?: string }; message?: string } | undefined;
  const apiMessage = data?.error?.message ?? data?.message;
  if (apiMessage) {
    return apiMessage;
  }

  const status = error.response?.status;
  const requestUrl = error.config?.url ?? url;
  return `Request failed ${status ? `(${status}) ` : ""}for ${requestUrl}: ${error.message}`;
}

export class ApiClient {
  private readonly axios: AxiosInstance;

  constructor(timeout = 15_000) {
    const baseURL = env.VITE_API_URL.replace(/\/+$/u, "");

    this.axios = axios.create({
      baseURL,
      timeout,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  public async makeRequest<T>({
    method,
    url,
    params,
    headers,
    data,
    signal,
  }: {
    method: Method;
    url: string;
    params?: Record<string, unknown> | URLSearchParams;
    headers?: Record<string, string>;
    data?: unknown;
    signal?: AbortSignal;
  }): Promise<ApiResponse<T>> {
    try {
      if (params && typeof params === "object" && !(params instanceof URLSearchParams)) {
        params = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== null && value !== undefined));
      }

      const config: AxiosRequestConfig = {
        method,
        url,
        params,
        data,
        headers,
      };

      if (signal) {
        config.signal = signal;
      }

      const response = await this.axios.request<ApiResponse<T>>(config);
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        throw new ApiRequestError(getApiErrorMessage(error, url), {
          status: error.response?.status,
          original: error,
        });
      }

      throw error;
    }
  }

  public get<T>({
    url,
    params,
    headers,
    signal,
  }: {
    url: string;
    params?: Record<string, unknown> | URLSearchParams;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  }) {
    return this.makeRequest<T>({ method: "GET", url, params, headers, signal });
  }

  public post<T>({
    url,
    data,
    params,
    headers,
    signal,
  }: {
    url: string;
    data?: unknown;
    params?: Record<string, unknown> | URLSearchParams;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  }) {
    return this.makeRequest<T>({ method: "POST", url, data, params, headers, signal });
  }

  public put<T>({
    url,
    data,
    params,
    headers,
    signal,
  }: {
    url: string;
    data?: unknown;
    params?: Record<string, unknown> | URLSearchParams;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  }) {
    return this.makeRequest<T>({ method: "PUT", url, data, params, headers, signal });
  }

  public patch<T>({
    url,
    data,
    params,
    headers,
    signal,
  }: {
    url: string;
    data?: unknown;
    params?: Record<string, unknown> | URLSearchParams;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  }) {
    return this.makeRequest<T>({ method: "PATCH", url, data, params, headers, signal });
  }

  public delete<T>({
    url,
    data,
    params,
    headers,
    signal,
  }: {
    url: string;
    data?: unknown;
    params?: Record<string, unknown> | URLSearchParams;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  }) {
    return this.makeRequest<T>({ method: "DELETE", url, data, params, headers, signal });
  }
}
